// A small, shared renderer for the markdown-lite convention AI-generated
// clinical documents use across this app: **bold**, *italic*/_italic_,
// "#"/"##" headings, "- "/"* "/"• " bullets, "---" dividers, and "Rx."
// prescription lines. Extracted from ConsultationChat.tsx/
// DischargeSummaryChat.tsx's near-identical local `renderMsg` — both only
// handled a whole-line "**bold**" heading, so inline bold mid-sentence and
// any markdown heading/bullet syntax the model reached for printed its
// literal symbols straight into the chat. Mirrors the same conventions
// backend/utils/docxGenerator.js converts to real Word formatting, so the
// in-app preview and the downloaded document read the same way.
import type { ReactNode } from 'react';

/** Inline spans within one line: **bold**, then italics (single-star or underscore) within whatever bold didn't already claim. */
export function renderInline(line: string): ReactNode[] {
  const boldParts = line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  const nodes: ReactNode[] = [];
  let key = 0;
  for (const part of boldParts) {
    if (part.startsWith('**') && part.endsWith('**')) {
      nodes.push(<strong key={key++}>{part.slice(2, -2)}</strong>);
      continue;
    }
    const italicParts = part.split(/(\*[^*]+\*|_[^_]+_)/g).filter(Boolean);
    for (const ip of italicParts) {
      const isItalic = (ip.startsWith('*') && ip.endsWith('*')) || (ip.startsWith('_') && ip.endsWith('_'));
      nodes.push(isItalic ? <em key={key++}>{ip.slice(1, -1)}</em> : <span key={key++}>{ip}</span>);
    }
  }
  return nodes;
}

const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const BULLET_RE = /^[-*•]\s+(.*)$/;
const TABLE_ROW_RE = /^\|?(.+?)\|?$/;
const TABLE_SEP_RE = /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?$/;

function splitTableRow(line: string): string[] {
  const inner = TABLE_ROW_RE.exec(line.trim())?.[1] ?? line;
  return inner.split('|').map(c => c.trim());
}

function renderTable(key: number, headerCells: string[], bodyRows: string[][]): ReactNode {
  return (
    <table key={key} style={{ borderCollapse: 'collapse', width: '100%', margin: '6px 0', fontSize: 12 }}>
      <thead>
        <tr>
          {headerCells.map((c, i) => (
            <th key={i} style={{ border: '1px solid #E5E7EB', background: '#F3F4F6', padding: '4px 8px', textAlign: 'left', fontWeight: 700 }}>
              {renderInline(c)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {bodyRows.map((row, r) => (
          <tr key={r}>
            {headerCells.map((_, i) => (
              <td key={i} style={{ border: '1px solid #E5E7EB', padding: '4px 8px' }}>{renderInline(row[i] ?? '')}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export interface LiteMarkdownOptions {
  /** Wraps each rendered line/block; defaults to a plain 1px-bottom-margin div, matching the chat's prior look. */
  lineStyle?: React.CSSProperties;
}

/** Renders a full message/document body as an array of block-level nodes. */
export function renderLiteMarkdown(content: string, opts: LiteMarkdownOptions = {}): ReactNode[] {
  const lineStyle = opts.lineStyle ?? { marginBottom: 1 };
  const lines = content.split('\n');
  const nodes: ReactNode[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;

    // A pipe-table: this line plus a following separator row, mirroring
    // backend/utils/docxGenerator.js's buildParagraphs so the in-app preview
    // and the downloaded document render the same table the same way.
    if (line.includes('|') && lines[i + 1] && TABLE_SEP_RE.test(lines[i + 1]!.trim())) {
      const headerCells = splitTableRow(line);
      i += 2;
      const bodyRows: string[][] = [];
      while (i < lines.length && lines[i]!.trim().includes('|')) {
        bodyRows.push(splitTableRow(lines[i]!));
        i++;
      }
      i--;
      nodes.push(renderTable(nodes.length, headerCells, bodyRows));
      continue;
    }

    if (line.startsWith('---')) {
      nodes.push(
        <div key={nodes.length} style={{ fontSize: 11, color: '#92400E', background: '#FEF3C7', borderRadius: 4, padding: '2px 8px', display: 'inline-block', marginTop: 6 }}>
          {line.replace(/-/g, '').trim()}
        </div>
      );
      continue;
    }
    if (line.startsWith('Rx.')) {
      nodes.push(<div key={nodes.length} style={{ fontFamily: 'monospace', fontSize: 12, padding: '2px 0', color: '#1D4ED8' }}>{line}</div>);
      continue;
    }
    if (line.startsWith('_(') && line.endsWith(')_')) {
      nodes.push(<div key={nodes.length} style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic', marginTop: 4 }}>{line.replace(/[_()]/g, '')}</div>);
      continue;
    }
    const trimmed = line.trim();
    if (/^\*\*[^*]+\*\*$/.test(trimmed)) {
      nodes.push(<div key={nodes.length} style={{ fontWeight: 700, marginTop: 6, marginBottom: 2 }}>{trimmed.slice(2, -2)}</div>);
      continue;
    }
    const headingMatch = HEADING_RE.exec(trimmed);
    if (headingMatch) {
      const level = headingMatch[1]!.length;
      const fontSize = level === 1 ? 15 : level === 2 ? 14 : 13;
      nodes.push(<div key={nodes.length} style={{ fontWeight: 700, fontSize, marginTop: 6, marginBottom: 2 }}>{renderInline(headingMatch[2]!)}</div>);
      continue;
    }
    const bulletMatch = BULLET_RE.exec(trimmed);
    if (bulletMatch) {
      nodes.push(
        <div key={nodes.length} style={{ display: 'flex', gap: 6, marginBottom: 1 }}>
          <span>•</span><span>{renderInline(bulletMatch[1]!)}</span>
        </div>
      );
      continue;
    }
    nodes.push(<div key={nodes.length} style={lineStyle}>{line ? renderInline(line) : <br />}</div>);
  }

  return nodes;
}
